import hash from "@emotion/hash"
import styled from "@emotion/styled"
import { useLink } from "@react-aria/link"
import * as uniform from "@uniformdev/context-react"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import { PopinButton } from "components/components"
import A11yModal from "components/footer/A11yModal"
import WeChatModal from "components/footer/WeChatModal"
import { Icon } from "components/icon/Icon"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"
import { useNavigation } from "contexts/Navigation"
import { bold } from "css/text"
import { AnimatePresence } from "framer-motion"
import { useId, useRef, useState } from "react"
import { createPortal } from "react-dom"

const cssPrimary = `css-${hash("is_primary")}`
const cssStartsNewGroup = `css-${hash("starts_new_group")}`

const Root = styled.li`
  > a,
  > span {
    ${bold}
    display: block;
    font-size: var(--baseline-font-size); // 14px > 16px

    &.${cssPrimary} {
      font-size: clamp(1.125rem, 0.875rem + 0.625vw, 1.625rem); // 18px > 26px
    }
  }

  > a {
    color: inherit;
    text-decoration: none;
    transition: color 0.3s;
    display: block;

    @media (hover: hover) {
      :hover {
        color: rgb(var(--green));
      }
    }
  }

  li {
    font-size: var(--baseline-font-size); // 14px > 16px
    font-weight: normal;

    &.${cssStartsNewGroup} {
      margin-top: var(--baseline);
    }

    a,
    button {
      all: unset;

      align-items: baseline; // center increased height, broke baseline grid
      color: rgb(var(--light-black));
      cursor: pointer;
      display: inline-flex;
      gap: 0.5rem;
      text-decoration: none;
      transition: color 0.3s;

      svg {
        fill: currentColor;
        height: 0.875rem;
        width: 0.875rem;
        transform: translateY(0.2ex); // tweak alignment because of align-items: baseline on parent
      }

      @media (hover: hover) {
        :hover {
          color: rgb(var(--green));
        }
      }

      &.reverseIcon {
        flex-direction: row-reverse;
      }

      &.inline {
        display: inline;

        svg {
          padding-inline-start: 0.5rem;
          height: 0.75rem;
          width: 0.75rem;
        }
      }
    }
  }
`

export default function Category({ label, href, is_primary, links, social, accessibility, wechat, modal }) {
  const console = useConsole()
  const scores = uniform.useScores()

  const rlink = useRef()

  const { linkProps } = useLink(rlink)
  const navigation = useNavigation()
  const uid = useId()
  const dictionary = useDictionary()

  const [isWeChatModalOpen, setIsWeChatModalOpen] = useState(false)
  const [isA11yModalOpen, setIsA11yModalOpen] = useState(false)
  // const [isContactFormOpen, setIsContactFormOpen] = useState(false)

  const bodyElement = process.browser && document.body

  const navigate = e => {
    e.preventDefault()
    navigation.navigate(e.target.getAttribute("href"))
  }

  const triggerEvent = (href, outlinkName) => {
    waTriggerEvent({
      eventName: "outlink",
      eventType: "Click",
      outlinkInfo: {
        linkURL: href,
        linkName: outlinkName,
      },
    })
  }

  const triggerFormItEvent = () => {
    waTriggerEvent({
      eventName: "FormItClicked",
      eventType: "Click",
    })
  }

  // const onOpenContactFormModal = e => {
  //   e.preventDefault()
  //   setIsContactFormOpen(true)
  // }

  // const onCloseContactFormModal = e => {
  //   setIsContactFormOpen(false)
  // }

  const onCloseWeChatModal = () => {
    setIsWeChatModalOpen(false)
  }

  const onOpenWeChatModal = e => {
    e.preventDefault()
    setIsWeChatModalOpen(true)

    // V7-2559: Send an event akin to an outlink
    triggerEvent("https://weixin.qq.com/r/LkjSykbE3xugrQKp9x08", "WeChat")
  }

  const onCloseA11yModal = () => {
    setIsA11yModalOpen(false)
  }

  const onOpenA11yModal = e => {
    e.preventDefault()
    setIsA11yModalOpen(true)
  }

  return (
    <Root>
      {href ? (
        <a id={uid} {...linkProps} ref={rlink} href={navigation.localize(href)} onClick={navigate} className={is_primary ? cssPrimary : null}>
          {label}
        </a>
      ) : (
        <span id={uid} className={is_primary ? cssPrimary : null}>
          {label}
        </span>
      )}
      <ul aria-labelledby={uid}>
        {links?.map(({ label, name, target, href, external, reference, outlink_name, starts_new_group, p13n }) => {
          if (p13n?.length) {
            for (const rule of p13n) {
              if (!rule.criteria?.name?.crit) {
                break
              }

              const hit = rule.criteria.name.crit.every(crit => {
                const { l: signal, op: operator, r: value } = crit

                switch (operator) {
                  case "=":
                    return +scores[signal] === +value
                  default:
                    return false
                }
              })

              if (rule.type === "include") {
                if (hit) continue
                else return null
              }

              if (rule.type === "exclude") {
                if (hit) return null
                else continue
              }
            }
          }

          return (
            <li key={label} className={starts_new_group ? cssStartsNewGroup : null}>
              {social ? (
                label.toLowerCase() === "wechat" ? (
                  <button type='button' aria-label={`${label} - ${dictionary.openInPopin()}`} onClick={onOpenWeChatModal}>
                    <Icon type={label.toLowerCase()} />
                    {name || label}
                  </button>
                ) : (
                  <a
                    {...linkProps}
                    aria-label={`${label} - ${dictionary.openInNewTab()}`}
                    href={href}
                    ref={rlink}
                    rel='noopener noreferrer'
                    target='_blank'
                    onClick={() => triggerEvent(href, label)}
                  >
                    <Icon type={label.toLowerCase()} />
                    {name || label}
                  </a>
                )
              ) : accessibility ? (
                <button type='button' onClick={onOpenA11yModal}>
                  {label}
                </button>
              ) : target === "external" ? (
                <a
                  className='inline reverseIcon'
                  {...linkProps}
                  ref={rlink}
                  href={external || href}
                  target='_blank'
                  rel='noopener noreferrer nofollow'
                  aria-label={`${label} - ${dictionary.openInNewTab()}`}
                  onClick={() => triggerEvent(external || href, outlink_name || label)}
                >
                  {label}
                  <Icon type='externalLink' />
                </a>
              ) : href === "#ContactForm" ? (
                reference && (
                  <PopinButton hasNoGestur triggerFormItEvent={triggerFormItEvent} stylesForCFI className='inline' label={label} reference={reference} />
                )
              ) : (
                // TODO: redo all the thingz
                <a {...linkProps} ref={rlink} href={navigation.localize(href)} onClick={navigate}>
                  {label}
                </a>
              )}
            </li>
          )
        })}
      </ul>

      {process.browser &&
        createPortal(<AnimatePresence>{isWeChatModalOpen && <WeChatModal {...wechat} onClose={onCloseWeChatModal} />}</AnimatePresence>, bodyElement)}
      {process.browser &&
        createPortal(<AnimatePresence>{isA11yModalOpen && <A11yModal {...modal} onCloseA11yModal={onCloseA11yModal} />}</AnimatePresence>, bodyElement)}
    </Root>
  )
}
